import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import "react-international-phone/style.css";

function TailwindPhoneInput({
  value,
  onChange,
  defaultCountry = "bg",
  error,
  ...props
}) {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry,
      value,
      onChange: (data) => {
        onChange(data.phone);
      },
    });

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <div
      className={`relative flex items-center w-full bg-gray-800 rounded-lg border ${
        error ? "border-red-500" : "border-gray-700"
      } ${props.className || ""}`}
    >
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          className="flex items-center justify-center px-3 py-2.5 border-r border-gray-700 bg-transparent focus:outline-none"
          onClick={() => setDropdownVisible(!dropdownVisible)}
        >
          <FlagImage iso2={country.iso2} className="w-5 h-3.5 object-cover" />
          <svg
            className="w-4 h-4 ml-1 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={dropdownVisible ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
            />
          </svg>
        </button>

        {dropdownVisible && (
          <div
            className="absolute z-50 top-full left-0 mt-1 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
            style={{ backgroundColor: "#1f2937" }}
          >
            <ul className="py-1">
              {defaultCountries.map((c) => {
                const countryItem = parseCountry(c);
                const isSelected = countryItem.iso2 === country.iso2;

                return (
                  <li
                    key={countryItem.iso2}
                    className={`flex items-center justify-between px-4 py-2.5 cursor-pointer ${
                      isSelected
                        ? "bg-blue-950 text-white"
                        : "hover:bg-blue-900 hover:text-white text-gray-200"
                    }`}
                    onClick={() => {
                      setCountry(countryItem.iso2);
                      setDropdownVisible(false);
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <FlagImage
                        iso2={countryItem.iso2}
                        className="w-5 h-3.5 object-cover"
                      />
                      <span className="text-sm font-medium">
                        {countryItem.name}
                      </span>
                    </div>
                    <span className="text-sm font-mono">
                      +{countryItem.dialCode}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <input
        className="w-full bg-transparent text-gray-200 border-0 focus:ring-0 focus:outline-none py-3 px-4"
        type="tel"
        ref={inputRef}
        value={inputValue}
        onChange={handlePhoneValueChange}
        placeholder={props.placeholder}
      />
    </div>
  );
}

TailwindPhoneInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  defaultCountry: PropTypes.string,
  error: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
};

export default React.memo(TailwindPhoneInput);
